import React, { createContext, useContext, useMemo } from 'react';

import { useDeployVersion } from './deployVersion';
import { useHasUnsavedChanges } from './unsavedChanges';

import type { ReactNode } from 'react';

import type { DeployVersion } from './deployVersion';
import type { ShellNotify } from './flash';
import type { GapReporter } from './gapReporter';
import type { SingleRootBootstrap } from './types';

export interface SingleRootContextValue {
  bootstrap: SingleRootBootstrap;
  reportGap: GapReporter;
  notify: ShellNotify;
  turnOff: () => Promise<void>;
  /** Whether web serves a newer build than this tab runs. Inert without `bootstrap.assetVersion`. */
  deployVersion: DeployVersion;
  /** True while any mounted `useUnsavedChangesGuard` or `useReportUnsavedChanges` reports unsaved changes. */
  dirty: boolean;
}

type SingleRootProviderProps = Omit<SingleRootContextValue, 'deployVersion' | 'dirty'> & {
  children: ReactNode;
};

const SingleRootContext = createContext<SingleRootContextValue | null>(null);

// Null outside the shell: shared components use this to stay inert on legacy pages.
export const useSingleRoot = (): SingleRootContextValue | null => useContext(SingleRootContext);

export const SingleRootProvider = ({
  bootstrap,
  children,
  notify,
  reportGap,
  turnOff
}: SingleRootProviderProps) => {
  const deployVersion = useDeployVersion(bootstrap.assetVersion);
  const dirty = useHasUnsavedChanges();
  const value = useMemo(
    () => ({ bootstrap, deployVersion, dirty, notify, reportGap, turnOff }),
    [bootstrap, deployVersion, dirty, notify, reportGap, turnOff]
  );
  return <SingleRootContext.Provider value={value}>{children}</SingleRootContext.Provider>;
};
